import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { LegalLinks } from "./LegalPages";
import { getAuthHeaders } from "../utils/api";
import NotificationPreferences from "./NotificationPreferences";
import { auth } from '@/lib/firebase';
import { 
  ChevronRight, ChevronDown, X, Camera, User, Settings, Globe, 
  Info, LogOut, RefreshCw, Plus, Star, Heart, Briefcase, Sparkles,
  FileText, Shield, Moon, Compass, AtSign, Phone, Mail, Facebook,
  HelpCircle, MessageCircle, BookOpen, Clock, Bell
} from "lucide-react";

// Debounce hook for text inputs
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  return debouncedCallback;
};

const SettingsPanel = ({ isOpen, onClose, onReset, onLogout, isGuest = false, onAvatarChange, onOpenAdmin }) => {
  const { language, setLanguage, t } = useLanguage();
  const [expandedSection, setExpandedSection] = useState("you"); // Default open section
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState(false);
  const [showCovenant, setShowCovenant] = useState(false);
  const [showZodiacInfo, setShowZodiacInfo] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [newHobby, setNewHobby] = useState("");
  const [occupationInput, setOccupationInput] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [usernameError, setUsernameError] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState(null);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const fileInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const profileCacheRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const CACHE_TTL = 30000;

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`${backendUrl}/api/admin/check`, {
          credentials: "include",
          headers
        });
        if (res.ok) {
          const data = await res.json();
          setIsAdmin(data.is_admin || false);
        }
      } catch (e) {
        console.log('Admin check failed:', e);
      }
    };
    if (isOpen) checkAdminStatus();
  }, [isOpen, backendUrl]);

  // Load profile on mount
  useEffect(() => {
    if (isOpen) {
      const now = Date.now();
      if (profileCacheRef.current && (now - lastFetchTimeRef.current) < CACHE_TTL) {
        setProfile(profileCacheRef.current);
        setIsLoading(false);
      } else {
        loadProfile();
      }
    }
  }, [isOpen]);

  // Sync inputs with profile
  useEffect(() => {
    if (profile?.occupation !== undefined) {
      setOccupationInput(profile.occupation || "");
    }
    if (profile?.username !== undefined) {
      setUsernameInput(profile.username || "");
    }
    if (profile?.email !== undefined) {
      setEmailInput(profile.email || "");
    }
    // Check if user signed in with Google
    const user = auth.currentUser;
    if (user) {
      const isGoogle = user.providerData?.some(p => p.providerId === 'google.com');
      setIsGoogleUser(isGoogle);
    }
  }, [profile?.occupation, profile?.username, profile?.email]);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/profile`, {
        credentials: "include",
        headers
      });
      if (response.ok) {
        const data = await response.json();
        const profileData = data.profile || {};
        setProfile(profileData);
        profileCacheRef.current = profileData;
        lastFetchTimeRef.current = Date.now();
      }
    } catch (err) {
      console.error("Failed to load profile:", err);
    }
    setIsLoading(false);
  };

  const saveField = async (field, value) => {
    setIsSaving(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/profile`, {
        method: "PATCH",
        headers,
        credentials: "include",
        body: JSON.stringify({ [field]: value })
      });
      if (response.ok) {
        const newProfile = { ...profile, [field]: value };
        setProfile(newProfile);
        profileCacheRef.current = newProfile;
        showSavedMessage();
        if (field === "username") {
          setUsernameError(null);
        }
        if (field === "email") {
          setEmailError(null);
        }
      } else if (response.status === 409) {
        // Username or email taken
        if (field === "username") {
          setUsernameError(isSpanish ? "Nombre de usuario ya tomado" : "Username already taken");
        } else if (field === "email") {
          setEmailError(isSpanish ? "Correo ya en uso" : "Email already in use");
        }
      } else if (response.status === 400) {
        const data = await response.json().catch(() => ({}));
        if (field === "email") {
          setEmailError(data.detail || (isSpanish ? "Correo inválido" : "Invalid email"));
        }
      }
    } catch (err) {
      console.error("Failed to save field:", err);
    }
    setIsSaving(false);
  };

  const debouncedSaveField = useDebounce((field, value) => {
    saveField(field, value);
  }, 800);

  const showSavedMessage = () => {
    setSavedMessage(true);
    setTimeout(() => setSavedMessage(false), 2000);
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target?.result;
      const newProfile = { ...profile, avatar_url: base64 };
      setProfile(newProfile);
      profileCacheRef.current = newProfile;
      if (onAvatarChange) {
        onAvatarChange(base64);
      }
      setIsSaving(true);
      try {
        const headers = await getAuthHeaders();
        await fetch(`${backendUrl}/api/profile`, {
          method: "PATCH",
          headers,
          credentials: "include",
          body: JSON.stringify({ avatar_url: base64 })
        });
        showSavedMessage();
      } catch (err) {
        console.error("Failed to save avatar:", err);
      }
      setIsSaving(false);
    };
    reader.readAsDataURL(file);
  };

  const handleAddHobby = () => {
    if (!newHobby.trim()) return;
    const currentHobbies = profile?.hobbies || [];
    if (!currentHobbies.includes(newHobby.trim())) {
      saveField("hobbies", [...currentHobbies, newHobby.trim()]);
    }
    setNewHobby("");
  };

  const handleRemoveHobby = (hobbyToRemove) => {
    const currentHobbies = profile?.hobbies || [];
    saveField("hobbies", currentHobbies.filter(h => h !== hobbyToRemove));
  };

  const handleLanguageChange = async (newLang) => {
    setLanguage(newLang);
    await saveField("language_context", newLang);
    
    const keysToDelete = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('astrum_house_') || 
        key.startsWith('astrum_profection_') ||
        key.startsWith('astrum_horoscope_') ||
        key.startsWith('astrum_energy_') ||
        key.startsWith('astrum_insight_')
      )) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => localStorage.removeItem(key));
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: newLang }));
  };

  const handleZodiacChange = async (system) => {
    try {
      const headers = await getAuthHeaders();
      await fetch(`${backendUrl}/api/settings/zodiac`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ zodiac_system: system })
      });
      setProfile(prev => ({ ...prev, zodiac_system: system }));
      showSavedMessage();
      window.dispatchEvent(new CustomEvent('zodiacSystemChanged', { detail: system }));
    } catch (err) {
      console.error("Failed to save zodiac preference:", err);
    }
  };

  const handleReset = () => {
    setShowResetConfirm(false);
    onClose();
    if (onReset) onReset();
  };

  const handleLogout = () => {
    onClose();
    if (onLogout) onLogout();
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (!isOpen) return null;

  const isSpanish = language === 'es';

  const formatDate = (dateStr) => {
    if (!dateStr) return isSpanish ? "No establecido" : "Not set";
    try {
      const date = new Date(dateStr);
      const userLocale = navigator.language || (isSpanish ? 'es-MX' : 'en-US');
      return date.toLocaleDateString(userLocale, {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return isSpanish ? "Desconocido" : "Unknown";
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const isPM = hours >= 12;
      const hour12 = hours % 12 || 12;
      const ampm = isPM ? 'PM' : 'AM';
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  // Format location with full details
  const formatLocation = (loc) => {
    if (!loc) return isSpanish ? "No establecido" : "Not set";
    return loc;
  };

  const languages = [
    { code: 'en', name: 'English', label: 'EN' },
    { code: 'es', name: 'Español', label: 'ES' },
  ];

  const houseSystemOptions = [
    { value: 'placidus', label: 'Placidus' },
    { value: 'porphyry', label: 'Porphyry' },
    { value: 'whole-sign', label: isSpanish ? 'Signo Completo' : 'Whole Sign' },
    { value: 'koch', label: 'Koch' },
  ];

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-gradient-to-b from-gray-900 to-black border border-primary/30 rounded-2xl w-full max-w-md mx-4 max-h-[85vh] overflow-hidden flex flex-col"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with User Avatar */}
        <div className="relative p-4 border-b border-primary/20">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div 
              className="relative cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
              data-testid="settings-avatar"
            >
              {profile?.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt="Profile" 
                  className="w-14 h-14 rounded-full object-cover border-2 border-primary/40 group-hover:border-primary transition-colors"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-primary/20 border-2 border-primary/40 flex items-center justify-center group-hover:border-primary transition-colors">
                  <User className="w-6 h-6 text-primary/60" />
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="w-3 h-3 text-black" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
                data-testid="photo-upload-input"
              />
            </div>
            
            {/* User Info */}
            <div className="flex-1">
              <h2 className="text-lg font-cinzel text-primary">
                {profile?.name || (isSpanish ? "Viajero Cósmico" : "Cosmic Traveler")}
              </h2>
              <p className="text-primary/60 text-xs font-inter">
                {profile?.username || (isSpanish ? "Sin nombre de usuario" : "No username")}
              </p>
              <p className="text-primary/50 text-xs font-inter">
                {profile?.sun_sign && `☉ ${profile.sun_sign}`}
                {profile?.moon_sign && ` · ☽ ${profile.moon_sign}`}
                {profile?.rising_sign && ` · ↑ ${profile.rising_sign}`}
              </p>
            </div>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-primary/60 hover:text-primary transition-colors p-1"
              data-testid="settings-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Saved Indicator */}
          <AnimatePresence>
            {savedMessage && (
              <motion.div
                className="absolute bottom-2 right-4 text-primary/60 text-xs font-inter"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                ✓ {isSpanish ? 'Guardado' : 'Saved'}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Language Toggle - Always Visible */}
        <div className="px-4 py-3 border-b border-primary/10 bg-black/20">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary/60" />
            <span className="text-primary/70 text-sm font-inter mr-2">
              {isSpanish ? 'Idioma' : 'Language'}:
            </span>
            <div className="flex gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-inter transition-all ${
                    language === lang.code
                      ? 'bg-primary/20 border border-primary text-primary'
                      : 'bg-black/30 border border-primary/20 text-primary/60 hover:border-primary/40'
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <span>{lang.label}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Scrollable Content - CoStar-style Sections */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* ========== YOU SECTION ========== */}
              <AccordionSection
                title={isSpanish ? "Tú" : "You"}
                subtitle={isSpanish ? "Tu información personal" : "Your personal info"}
                icon={<User className="w-4 h-4" />}
                isExpanded={expandedSection === "you"}
                onToggle={() => toggleSection("you")}
              >
                <div className="space-y-4">
                  {/* Name */}
                  <SettingsRow 
                    label={isSpanish ? "Nombre" : "Name"} 
                    value={profile?.name || (isSpanish ? "No establecido" : "Not set")} 
                    icon={<User className="w-4 h-4" />}
                  />
                  
                  {/* Username - Editable */}
                  <div className="space-y-2">
                    <label className="text-primary/70 text-sm font-inter flex items-center gap-2">
                      <AtSign className="w-3.5 h-3.5" />
                      {isSpanish ? "Nombre de usuario" : "Username"}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={usernameInput}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (!val.startsWith("@")) val = "@" + val;
                          setUsernameInput(val);
                          setUsernameError(null);
                          debouncedSaveField('username', val);
                        }}
                        placeholder="@yourname123"
                        className={`w-full px-3 py-2 pr-20 bg-black/30 border rounded-lg text-primary text-sm font-inter placeholder:text-primary/30 focus:outline-none ${
                          usernameError ? 'border-red-500/50' : 'border-primary/20 focus:border-primary/40'
                        }`}
                        data-testid="username-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-primary/40 font-inter">
                        {isSaving ? (isSpanish ? "Guardando..." : "Saving...") : ""}
                      </span>
                    </div>
                    {usernameError && (
                      <p className="text-red-400 text-xs">{usernameError}</p>
                    )}
                    <p className="text-primary/40 text-xs">
                      {isSpanish ? "Único, puedes cambiarlo después" : "Unique, you can change it later"}
                    </p>
                  </div>

                  {/* Email - Editable for email/password users only */}
                  <div>
                    <label className="text-primary/70 text-sm font-inter mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {isSpanish ? "Correo electrónico" : "Email"}
                    </label>
                    {isGoogleUser ? (
                      <>
                        <div className="bg-black/30 border border-primary/20 rounded-lg p-3 text-primary/80 text-sm">
                          {profile?.email || "—"}
                        </div>
                        <p className="text-primary/40 text-xs mt-1 flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {isSpanish ? "Gestionado por Google" : "Managed by Google"}
                        </p>
                      </>
                    ) : (
                      <>
                        <input
                          type="email"
                          value={emailInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEmailInput(val);
                            setEmailError(null);
                          }}
                          onBlur={() => {
                            if (emailInput && emailInput !== profile?.email) {
                              saveField('email', emailInput);
                            }
                          }}
                          placeholder={isSpanish ? "tu@correo.com" : "your@email.com"}
                          className={`w-full bg-black/30 border rounded-lg p-3 text-primary/80 text-sm placeholder:text-primary/30 ${
                            emailError ? 'border-red-500/50' : 'border-primary/20 focus:border-primary/40'
                          }`}
                          data-testid="email-input"
                        />
                        {emailError && (
                          <p className="text-red-400 text-xs mt-1">{emailError}</p>
                        )}
                        <p className="text-primary/40 text-xs mt-1">
                          {isSpanish ? "Puedes cambiar tu correo aquí" : "You can change your email here"}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </AccordionSection>

              {/* ========== CHART (ASTRAL IDENTITY) SECTION ========== */}
              <AccordionSection
                title={isSpanish ? "Carta" : "Chart"}
                subtitle={isSpanish ? "Tu identidad astral" : "Your astral identity"}
                icon={<Star className="w-4 h-4" />}
                isExpanded={expandedSection === "chart"}
                onToggle={() => toggleSection("chart")}
              >
                <div className="space-y-3">
                  <SettingsRow 
                    label={isSpanish ? "Fecha de nacimiento" : "Birth Date"} 
                    value={formatDate(profile?.birth_date)} 
                  />
                  <SettingsRow 
                    label={isSpanish ? "Hora de nacimiento" : "Birth Time"} 
                    value={formatTime(profile?.birth_time)} 
                  />
                  <SettingsRow 
                    label={isSpanish ? "Lugar de nacimiento" : "Birth Place"} 
                    value={formatLocation(profile?.location)} 
                  />
                  
                  {/* House Systems */}
                  <div className="mt-4 pt-4 border-t border-primary/10">
                    <label className="text-primary/70 text-sm font-inter mb-3 block">
                      {isSpanish ? "Sistema de Casas" : "House Systems"}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {houseSystemOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => saveField('house_system', option.value)}
                          className={`p-2 rounded-lg border transition-all text-center ${
                            profile?.house_system === option.value
                              ? 'border-primary bg-primary/10'
                              : 'border-primary/20 bg-black/30 hover:border-primary/40'
                          }`}
                          data-testid={`house-${option.value}`}
                        >
                          <span className="text-primary text-xs font-inter">{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Zodiac System Toggle */}
                  <div className="mt-4 pt-4 border-t border-primary/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-primary/70 text-sm font-inter">
                          {isSpanish ? "Sistema Zodiacal" : "Zodiac System"}
                        </span>
                        <button
                          onClick={() => setShowZodiacInfo(true)}
                          className="text-primary/40 hover:text-primary transition-colors"
                          data-testid="zodiac-info-btn"
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <ZodiacOption
                        selected={profile?.zodiac_system !== 'sidereal'}
                        label={isSpanish ? "Tropical ☀️" : "Tropical ☀️"}
                        sublabel={isSpanish ? "Occidental" : "Western"}
                        onClick={() => handleZodiacChange('tropical')}
                      />
                      <ZodiacOption
                        selected={profile?.zodiac_system === 'sidereal'}
                        label={isSpanish ? "Sideral ⭐" : "Sidereal ⭐"}
                        sublabel={isSpanish ? "Védico" : "Vedic"}
                        onClick={() => handleZodiacChange('sidereal')}
                      />
                    </div>
                  </div>

                  {/* Override Time Zone (placeholder) */}
                  <button
                    className="w-full flex items-center gap-3 py-3 px-3 mt-4 bg-black/30 border border-primary/20 rounded-lg text-primary/50 cursor-not-allowed"
                    disabled
                  >
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-inter">
                      {isSpanish ? "Anular zona horaria" : "Override time zone"}
                    </span>
                    <span className="ml-auto text-xs text-primary/30">
                      {isSpanish ? "Próximamente" : "Coming soon"}
                    </span>
                  </button>
                  
                  {/* View Full Chart Button */}
                  <button
                    onClick={() => {
                      // Close settings and open natal chart
                      // We'll dispatch a custom event that the parent can listen to
                      window.dispatchEvent(new CustomEvent('openNatalChart'));
                      onClose?.();
                    }}
                    className="w-full flex items-center justify-center gap-2 py-4 px-3 mt-4 rounded-xl transition-all hover:scale-[1.01]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)',
                      border: '1px solid rgba(212,175,55,0.4)'
                    }}
                    data-testid="view-full-chart-btn"
                  >
                    <Star className="w-4 h-4 text-primary" />
                    <span className="text-sm font-cinzel text-primary">
                      {isSpanish ? "Ver Mi Carta Natal Completa" : "View My Full Birth Chart"}
                    </span>
                  </button>
                </div>
              </AccordionSection>

              {/* ========== PREMIUM SECTION (Placeholder) ========== */}
              <AccordionSection
                title="Premium"
                subtitle={isSpanish ? "Suscripciones y compras" : "Subscriptions & purchases"}
                icon={<Sparkles className="w-4 h-4" />}
                isExpanded={expandedSection === "premium"}
                onToggle={() => toggleSection("premium")}
              >
                <div className="py-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500/20 to-primary/20 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-primary/60" />
                  </div>
                  <h4 className="text-primary font-cinzel mb-2">
                    {isSpanish ? "Próximamente" : "Coming Soon"}
                  </h4>
                  <p className="text-primary/50 text-sm font-inter">
                    {isSpanish 
                      ? "Características premium y lecturas exclusivas" 
                      : "Premium features and exclusive readings"}
                  </p>
                </div>
              </AccordionSection>

              {/* ========== NOTIFICATIONS SECTION (Refined Oracle) ========== */}
              <AccordionSection
                title={isSpanish ? "Notificaciones" : "Notifications"}
                subtitle={isSpanish ? "Controla cómo te contacta el Oráculo" : "Control how The Oracle reaches you"}
                icon={<Bell className="w-4 h-4" />}
                isExpanded={expandedSection === "notifications"}
                onToggle={() => toggleSection("notifications")}
              >
                <NotificationPreferences 
                  language={language} 
                  onClose={() => toggleSection(null)}
                />
              </AccordionSection>

              {/* ========== SUPPORT SECTION ========== */}
              <AccordionSection
                title={isSpanish ? "Soporte" : "Support"}
                subtitle={isSpanish ? "Ayuda y recursos" : "Help & resources"}
                icon={<HelpCircle className="w-4 h-4" />}
                isExpanded={expandedSection === "support"}
                onToggle={() => toggleSection("support")}
              >
                <div className="space-y-2">
                  <SupportButton
                    icon={<MessageCircle className="w-4 h-4" />}
                    label={isSpanish ? "Enviar comentarios" : "Send Feedback"}
                    sublabel={isSpanish ? "Cuéntanos tu experiencia" : "Tell us about your experience"}
                    onClick={() => window.open('mailto:support@astralbelonging.com', '_blank')}
                  />
                  <SupportButton
                    icon={<Heart className="w-4 h-4" />}
                    label={isSpanish ? "Recursos de salud mental" : "Mental health resources"}
                    sublabel={isSpanish ? "Apoyo y bienestar" : "Support & wellness"}
                    onClick={() => {}}
                    disabled
                  />
                  <SupportButton
                    icon={<Phone className="w-4 h-4" />}
                    label={isSpanish ? "Línea de crisis" : "Crisis text line"}
                    sublabel={isSpanish ? "Ayuda 24/7 disponible" : "24/7 help available"}
                    onClick={() => {}}
                    disabled
                  />
                  <SupportButton
                    icon={<BookOpen className="w-4 h-4" />}
                    label={isSpanish ? "Aprende astrología" : "Learn astrology"}
                    sublabel={isSpanish ? "Guías y tutoriales" : "Guides & tutorials"}
                    onClick={() => {}}
                    disabled
                  />
                  <SupportButton
                    icon={<FileText className="w-4 h-4" />}
                    label="FAQ"
                    sublabel={isSpanish ? "Preguntas frecuentes" : "Frequently asked questions"}
                    onClick={() => {}}
                    disabled
                  />
                </div>
              </AccordionSection>

              {/* ========== LOGOUT & DANGER ZONE ========== */}
              <div className="pt-4 space-y-3">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 py-3 px-4 bg-primary/10 border border-primary/20 rounded-lg text-primary hover:bg-primary/20 transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-inter">
                    {isSpanish ? "Cerrar Sesión" : "Log Out"}
                  </span>
                </button>

                {/* Admin Panel Button - Only visible to admins */}
                {isAdmin && onOpenAdmin && (
                  <button
                    onClick={() => {
                      onClose();
                      onOpenAdmin();
                    }}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 transition-colors"
                    data-testid="admin-panel-btn"
                  >
                    <Shield className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-inter text-red-400">
                      {isSpanish ? "Panel de Administrador" : "Admin Panel"}
                    </span>
                  </button>
                )}

                {/* Legal Links & Version */}
                <div className="flex flex-col items-center gap-3 pt-4 border-t border-primary/10">
                  <LegalLinks language={language} />
                  
                  <button
                    onClick={() => setShowCovenant(true)}
                    className="text-primary/40 hover:text-primary/60 text-xs underline"
                    data-testid="privacy-btn"
                  >
                    {isSpanish ? "Política de Privacidad" : "Privacy Policy"}
                  </button>
                  
                  <button
                    onClick={() => setShowResetConfirm(true)}
                    className="text-rose-400/60 hover:text-rose-400 text-xs"
                    data-testid="delete-account-btn"
                  >
                    {isSpanish ? "Eliminar cuenta" : "Delete Account"}
                  </button>
                  
                  <p className="text-primary/30 text-xs font-inter">
                    v1.0.0 · Astral Belonging
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Modals */}
      <AnimatePresence>
        {showZodiacInfo && <ZodiacInfoModal onClose={() => setShowZodiacInfo(false)} isSpanish={isSpanish} />}
        {showCovenant && <CovenantModal onClose={() => setShowCovenant(false)} isSpanish={isSpanish} />}
        {showResetConfirm && (
          <ResetConfirmModal
            onClose={() => setShowResetConfirm(false)}
            onConfirm={handleReset}
            isSpanish={isSpanish}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ==================== HELPER COMPONENTS ====================

const AccordionSection = ({ title, subtitle, icon, isExpanded, onToggle, children }) => (
  <div className="bg-black/20 border border-primary/10 rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-primary/5 transition-colors"
      data-testid={`accordion-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-primary/60">{icon}</span>
        <div className="text-left">
          <h3 className="text-primary font-cinzel text-sm">{title}</h3>
          {subtitle && <p className="text-primary/40 text-xs font-inter">{subtitle}</p>}
        </div>
      </div>
      <motion.div
        animate={{ rotate: isExpanded ? 180 : 0 }}
        transition={{ duration: 0.2 }}
      >
        <ChevronDown className="w-4 h-4 text-primary/60" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-4 pt-2 border-t border-primary/10">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const SettingsRow = ({ label, value, icon }) => (
  <div className="flex justify-between items-center py-2 border-b border-primary/5 last:border-0">
    <div className="flex items-center gap-2">
      {icon && <span className="text-primary/40">{icon}</span>}
      <span className="text-primary/50 text-sm font-inter">{label}</span>
    </div>
    <span className="text-primary text-sm font-inter text-right max-w-[60%] truncate">{value}</span>
  </div>
);

const SupportButton = ({ icon, label, sublabel, onClick, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center gap-3 py-3 px-3 rounded-lg transition-colors ${
      disabled 
        ? 'bg-black/20 text-primary/30 cursor-not-allowed' 
        : 'bg-black/30 border border-primary/20 text-primary/70 hover:border-primary/40'
    }`}
  >
    {icon}
    <div className="text-left flex-1">
      <span className="text-sm font-inter block">{label}</span>
      {sublabel && <span className="text-xs text-primary/40">{sublabel}</span>}
    </div>
    {!disabled && <ChevronRight className="w-4 h-4" />}
  </button>
);

const ZodiacOption = ({ selected, label, sublabel, onClick }) => (
  <button
    onClick={onClick}
    className={`p-3 rounded-lg border transition-all text-center ${
      selected
        ? 'border-primary bg-primary/10'
        : 'border-primary/20 bg-black/30 hover:border-primary/40'
    }`}
    data-testid={`zodiac-${label.includes('Tropical') ? 'tropical' : 'sidereal'}`}
  >
    <span className="text-primary text-sm font-inter block">{label}</span>
    <span className="text-primary/50 text-[10px] font-inter">{sublabel}</span>
  </button>
);

// Zodiac Info Modal
const ZodiacInfoModal = ({ onClose, isSpanish }) => (
  <motion.div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-md"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="bg-gradient-to-b from-gray-900/95 to-black/95 border border-primary/40 rounded-2xl w-full max-w-lg mx-4 overflow-hidden"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-6">
        <h3 className="text-primary font-cinzel text-lg mb-4">
          {isSpanish ? "Sistemas Zodiacales" : "Zodiac Systems"}
        </h3>
        <div className="space-y-4 text-sm font-inter">
          <div>
            <h4 className="text-primary font-semibold mb-1">☀️ {isSpanish ? "Tropical (Occidental)" : "Tropical (Western)"}</h4>
            <p className="text-primary/60">
              {isSpanish 
                ? "Basado en las estaciones. El más usado en occidente. Tu signo solar se basa en la posición del Sol relativa a los equinoccios."
                : "Based on seasons. Most used in the West. Your sun sign is based on the Sun's position relative to the equinoxes."}
            </p>
          </div>
          <div>
            <h4 className="text-primary font-semibold mb-1">⭐ {isSpanish ? "Sideral (Védico)" : "Sidereal (Vedic)"}</h4>
            <p className="text-primary/60">
              {isSpanish 
                ? "Basado en las constelaciones reales. Usado en la astrología védica india. Los signos están ~23° desfasados del tropical."
                : "Based on actual constellations. Used in Indian Vedic astrology. Signs are ~23° offset from tropical."}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="mt-6 w-full py-2 bg-primary/20 border border-primary/40 rounded-lg text-primary font-inter hover:bg-primary/30 transition-colors"
        >
          {isSpanish ? "Entendido" : "Got it"}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// Covenant Modal
const CovenantModal = ({ onClose, isSpanish }) => (
  <motion.div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-md"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="bg-gradient-to-b from-gray-900/95 to-black/95 border border-primary/40 rounded-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden flex flex-col"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-6 border-b border-primary/20">
        <h3 className="text-primary font-cinzel text-lg">
          {isSpanish ? "El Pacto Cósmico" : "The Cosmic Covenant"}
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto p-6 text-sm font-inter text-primary/70 space-y-4">
        <p>
          {isSpanish 
            ? "Al usar Astral Belonging, aceptas las siguientes condiciones de tu viaje cósmico:"
            : "By using Astral Belonging, you accept the following conditions of your cosmic journey:"}
        </p>
        <ul className="list-disc list-inside space-y-2">
          <li>{isSpanish ? "Tus datos de nacimiento se usan para cálculos astrológicos" : "Your birth data is used for astrological calculations"}</li>
          <li>{isSpanish ? "Las lecturas son para entretenimiento y guía espiritual" : "Readings are for entertainment and spiritual guidance"}</li>
          <li>{isSpanish ? "Tu información se almacena de forma segura" : "Your information is stored securely"}</li>
          <li>{isSpanish ? "Puedes eliminar tus datos en cualquier momento" : "You can delete your data at any time"}</li>
        </ul>
      </div>
      <div className="p-6 border-t border-primary/20">
        <button
          onClick={onClose}
          className="w-full py-2 bg-primary/20 border border-primary/40 rounded-lg text-primary font-inter hover:bg-primary/30 transition-colors"
        >
          {isSpanish ? "Acepto el Pacto" : "I Accept the Covenant"}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// Reset Confirm Modal
const ResetConfirmModal = ({ onClose, onConfirm, isSpanish }) => (
  <motion.div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-md"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="bg-gradient-to-b from-gray-900/95 to-black/95 border border-rose-500/40 rounded-2xl w-full max-w-sm mx-4 overflow-hidden"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-6 text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-rose-500/20 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 text-rose-400" />
        </div>
        <h3 className="text-primary font-cinzel text-lg mb-2">
          {isSpanish ? "¿Eliminar Cuenta?" : "Delete Account?"}
        </h3>
        <p className="text-primary/60 text-sm font-inter mb-6">
          {isSpanish 
            ? "Esto borrará todos tus datos permanentemente. Esta acción no se puede deshacer."
            : "This will permanently delete all your data. This action cannot be undone."}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-primary/10 border border-primary/20 rounded-lg text-primary font-inter hover:bg-primary/20 transition-colors"
          >
            {isSpanish ? "Cancelar" : "Cancel"}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2 bg-rose-500/20 border border-rose-500/40 rounded-lg text-rose-400 font-inter hover:bg-rose-500/30 transition-colors"
            data-testid="confirm-delete-btn"
          >
            {isSpanish ? "Eliminar" : "Delete"}
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

export default SettingsPanel;
